import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import '../styles/auth-pages.css';
import { signUpUser } from '../store/authSlice';
import { RootState, AppDispatch } from '../store/store';
import StepCarousel from '../components/Landing/StepCarousel';
import { leaseCreationSteps } from '../components/Landing/LeaseStepsSection';

const SignupPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading, error, isAuthenticated } = useSelector((state: RootState) => state.auth);

  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const steps = leaseCreationSteps;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const emailParam = params.get('email');

    if (emailParam) {
      setFormData((previous) => {
        if (previous.email) {
          return previous;
        }

        return {
          ...previous,
          email: emailParam,
        };
      });
    }
  }, [location.search]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    try {
      await dispatch(signUpUser({
        email: formData.email,
        password: formData.password,
      })).unwrap();

      if (window.gtag) {
        window.gtag('event', 'conversion', {
          send_to: 'AW-17359198207/2B0vCMq9-6gbEP-vwdVA'
        });
      }
    } catch (submitError) {
      console.error('Signup failed:', submitError);
    }
  };
  return (
    <div className="auth-page">
      <div className="auth-page__container">
        <div className="auth-page__grid">
          <div className="auth-page__copy auth-page__copy--signup">
            <h1 className="auth-page__title auth-page__title--steps">
              Create your lease in <span>5 easy steps</span>
            </h1>
            <div className="auth-steps-carousel" aria-live="polite">
              <StepCarousel
                steps={steps}
                badgeText="Step"
                featuresLabel="Features"
                alignStart
              />
            </div>
          </div>

          <div className="auth-card auth-card--expanded auth-card--signup">
            <div className="auth-card__header">
              <h2 className="auth-card__title">Sign up</h2>
            </div>

            {error && <div className="auth-error">{error}</div>}

            <form className="auth-form" onSubmit={handleSubmit}>
              <div className="auth-input-group">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  className="auth-input"
                  placeholder="landlord@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="auth-input-group">
                <label htmlFor="password">Password</label>
                <div className="auth-input-wrapper">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    className="auth-input"
                    placeholder="Create a secure password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((previous) => !previous)}
                    className="auth-password-toggle"
                    disabled={isLoading}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              <button type="submit" className="auth-submit" disabled={isLoading}>
                {isLoading ? 'Creating account...' : 'Create account'}
              </button>
              <p className="auth-legal-copy">
                By creating an account you agree to our <Link to="/terms">Terms & Legal Notice</Link>.
              </p>
            </form>

            <div className="auth-card__supporting auth-card__supporting--tight">
              <p className="auth-switch">Already have an account? <Link to="/login">Log in</Link></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
